import { Box, Divider, Grid } from "@mui/material";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import { fetchMyPlanDetailAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { useTranslate } from "hooks/useTranslate";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { nestedTernaryCheckerCond2 } from "utils/CommonChecker";
import { PAGE_SIZE, allPlansType, brandDeadlinesType, initiativePlansType, tabConstants } from "utils/CommonConstants";
import { getHltInncodes, userNameToHltId } from "utils/CommonFunctions";
import {
  clearPlanProgress,
  fetchAssigneeDetailsByHtlIdsAsync,
  fetchMyPlansAsync,
  fetchPlanProgressMultiTargetDetails,
  setLoader,
  setSelectedMultiTarget
} from "../../AboveProperty.slice";
import FilterButtons from "../../FilterButtons";
import AssignPlan from "../AssignPlan";
import PlanDetails from "./PlanDetails";
import PlansProgress from "./PlansProgress";
import UnImplementedProperties from "./UnImplementedProperties";

const PlanProgress = () => {
  const translate = useTranslate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const {
    totalList,
    plansList: userplans,
    inncodes: inncodesList
  } = useAppSelector((state) => state.aboveProperty.planProgress);
  const {
    abovePropertyLoader,
    roleCategoryList,
    multiTargetDetails: multiTargetInfo
  } = useAppSelector((state) => state.aboveProperty);
  const [pageNo, setPageNo] = useState(0);
  const [multiplansAssign, setMultiplansAssign] = useState(false);
  const multiTargetDetails = useAppSelector((state) => state.aboveProperty.planProgressMultiTarget);
  const myPlanDetails: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const { source, status, searchText, owners, inncode, roleCategory } = useAppSelector(
    (state: any) => state.aboveProperty.filters.planViewFilters.planProgressFilters
  );
  const ownerOptions = uniqBy(roleCategoryList, "hltId");
  const [toggleOn, setToggleOn] = useState<boolean>(true);
  const dispatch = useAppDispatch();
  const redirectFrom = searchParams.get("redirectFrom");
  const isTemplatePlan = redirectFrom === tabConstants.AssignedCustomPlans;
  const location = useLocation();
  const progressInncodes = location?.state?.progressInncodes;
  const isdeadlineOrInitiative =
    tabConstants.BrandDeadline === redirectFrom || tabConstants.BrandInitiative === redirectFrom;
  const getProps = (startPage?: number) => {
    if (startPage === 0) setPageNo(startPage);
    const props = {
      innCode: nestedTernaryCheckerCond2(
        inncode?.length,
        location?.state?.watchListPlan,
        inncode?.join(","),
        localStorage?.getItem("hltInnCodes"),
        progressInncodes?.join(",")
      ),
      includeOwners: true,
      includeFilters: true,
      searchedPropertyText: searchText,
      planSources: source.join(","),
      status: status.join(","),
      roleCategory: roleCategory?.join(","),
      searchedHltId: userNameToHltId(owners, roleCategoryList)?.join(","),
      applyOwnerFilter: !!owners?.length
    };
    const idValue = isTemplatePlan ? { templatePlanIds: [Number(params.id)] } : { cmsId: params.id };
    const page = startPage ?? pageNo;
    const payload = { ...props, ...idValue };
    return { payload, page };
  };

  const getMultiTarget = async () => {
    if (tabConstants.libraryPlans === redirectFrom || !multiTargetInfo.length)
      await dispatch(
        fetchPlanProgressMultiTargetDetails({
          innCodes: getHltInncodes(),
          filter: {
            functionalAreaIds: [],
            filterIds: [],
            ids: [params?.id]
          },
          pagination: {
            page: 1,
            size: PAGE_SIZE
          },
          planType: [allPlansType, brandDeadlinesType, initiativePlansType],
          isSortingDisabled: true
        })
      );
    else {
      dispatch(setSelectedMultiTarget(params?.id));
    }
  };

  const getPlanProgress = async (props: any) => {
    const plans = await dispatch(fetchMyPlansAsync(props));
    const planOwners = plans?.payload?.meta?.owners;
    if (
      [source, status, searchText, owners, inncode, roleCategory].every((el) => el?.length === 0) &&
      planOwners?.length
    ) {
      const data = {
        value: planOwners.join(","),
        size: planOwners?.length
      };
      await dispatch(fetchAssigneeDetailsByHtlIdsAsync(data));
    }
    dispatch(setLoader(false));
  };
  useEffect(() => {
    if (pageNo > 0) {
      dispatch(fetchMyPlansAsync(getProps()));
    }
  }, [pageNo]);

  useEffect(() => {
    if (isTemplatePlan) {
      dispatch(fetchMyPlanDetailAsync({ id: params?.id }));
    } else getMultiTarget();

    return () => {
      dispatch(clearPlanProgress());
    };
  }, []);

  useEffect(() => {
    getPlanProgress(getProps(0));
  }, [source, status, searchText, owners, inncode, roleCategory]);

  const fetchPlans = async (page?: number) => {
    await dispatch(fetchMyPlansAsync(getProps(page)));
    dispatch(setLoader(false));
  };

  const handleCloseDialog = () => {
    setMultiplansAssign(false);
  };
  const handleToggle = async (val: boolean) => {
    setToggleOn(val);
    if (val) fetchPlans(0);
  };

  return (
    <>
      {abovePropertyLoader && <Spinner backdropVisible />}

      <PlanDetails isTemplatePlan={isTemplatePlan} setMultiplansAssign={(val: boolean) => setMultiplansAssign(val)} />
      {
        <>
          <Grid sx={{ marginTop: "24px", marginBottom: "24px" }}>
            {" "}
            <Typography variant={"h5"} color={tokens.Text.General.PrimaryHeadline} showBold>
              {translate("dashboard.planInProgress")}
            </Typography>
          </Grid>
          <Box
            sx={{
              backgroundColor: tokens.Background.General.Primary,
              borderRadius: "12px"
            }}
          >
            {isdeadlineOrInitiative && !toggleOn ? (
              <>
                {" "}
                <UnImplementedProperties
                  toggleOn={toggleOn}
                  handleToggle={(val: boolean) => {
                    handleToggle(val);
                  }}
                  cmsId={params?.id}
                />
              </>
            ) : (
              <>
                {" "}
                <FilterButtons
                  isSearchBarVisible
                  isSourceVisible
                  isOwnersVisible
                  isInncodeVisible
                  isRoleCategoryVisible
                  optionsList={{
                    inncode: location?.state?.watchListPlan ? inncodesList : progressInncodes,
                    owner: ownerOptions
                  }}
                  isStatusVisible
                  isToggleVisible={isdeadlineOrInitiative}
                  toggleOnChange={(val: boolean) => {
                    handleToggle(val);
                  }}
                  parentView="planViewFilters"
                  filterType="planProgressFilters"
                  searchBarPlaceHolder="Search by property"
                />
                <Divider />
                <PlansProgress
                  data={userplans}
                  totalList={totalList}
                  pagination={{ pageNo: pageNo, setPageNo: setPageNo }}
                />
              </>
            )}
          </Box>
        </>
      }
      {multiplansAssign && (
        <AssignPlan
          setOpenAssignPlanDialog={setMultiplansAssign}
          handleCloseDialog={handleCloseDialog}
          selectedPlanDetail={isTemplatePlan ? myPlanDetails : multiTargetDetails}
          isTemplatePlan={isTemplatePlan}
          flowSpecificPayload={{
            isBulkAssignment: true,
            innCode: isTemplatePlan ? "" : multiTargetDetails?.properties[0]
          }}
        />
      )}
    </>
  );
};

export default PlanProgress;
